import React, { useCallback, useMemo, useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Breadcrumbs, Button, Modal } from "@truevault/aquilo";
import { EyeIcon } from "@heroicons/react/24/solid";
import { Routes } from "../../../root/routes";
import { usePrimaryOrganization } from "../../../hooks";
import { OptOutType } from "../../../../common/service/server/dto/PrivacyCenterDto";
import { usePrivacyCenterSettings } from "../../../components/superorg/settings/PrivacyCenterSettings";
import {
  CustomFieldEnabledType,
  PrivacyCenterRequestFormSettingsRow,
} from "../../../components/organisms/settings/rows/PrivacyCenterRequestFormSettingsRow";
import { PrivacyCenterOptOutSettingsRow } from "../../../components/organisms/settings/rows/PrivacyCenterOptOutSettingsRow";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../PrivacyNoticeChecklistItems";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { useHistory } from "react-router";
import { usePrivacyCenterPreviewPopup } from "../StayCompliantPrivacyCenterPage";
import { useEditPrivacyCenter } from "../../../components/privacyCenter/EditPrivacyCenter";
import { PrivacyCenterOptOutNoticeEditorRow } from "../../../components/organisms/settings/rows/PrivacyCenterOptOutNoticeEditorRow";
import { PrivacyCenterResidencySettingsRow } from "../../../components/organisms/settings/rows/PrivacyCenterResidencySettingsRow";
import "../../../../../sass/pages/dsar-forms.scss";

export const DevInstructionsPoliciesAndNoticesOnboardingSlug =
  "finished-policies-notices-onboarding";

export const DevInstructionsDSARFormsOnboardingSlug = "finished-dsar-forms-onboarding";

export function DSARFormsPage() {
  const history = useHistory();

  const [org] = usePrimaryOrganization();
  const { settings } = usePrivacyCenterSettings();
  const editPrivacyCenter = useEditPrivacyCenter();

  const privacyCenter = editPrivacyCenter?.props?.privacyCenter;

  const { answers: devInstructionsAnswers, setAnswer: setDevInstructionsAnswer } = useSurvey(
    org?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  const showOptOutFormField = useMemo(() => {
    if (privacyCenter?.optOutFormOptions?.optOutType != OptOutType.NONE) return true;
    return false;
  }, [privacyCenter]);

  const showOptOutNoticeEditor = useMemo(() => {
    if (privacyCenter?.optOutFormOptions?.optOutType != OptOutType.NONE) return true;
    return false;
  }, [privacyCenter]);

  const [hasRequestInFlight, setHasRequestInFlight] = useState<boolean>(false);
  // Before it hits the backend, have the request handler indicate that we're submitting the form so other forms on this
  // page can switch to a loading state. Ideally we'd do this through NetworkRequestState, but we'd have to plumb it all
  // the way from the call to `usePrivacyCenter` a few parent components up.
  const wrapRequestHandler = useCallback((handler) => {
    return async (...args) => {
      setHasRequestInFlight(true);
      try {
        await handler(...args);
      } finally {
        // This is a workaround to a more fundamental issue with the way privacy center updates are done. Essentially we
        // do a PUT to save the value and in the `onSuccess` of the API layer, and there is an immediate `GET` to update
        // local form values to reflect the new state. On slow enough connections, the PUT finishes, releasing the await
        // of the update promise, but the GET comes later. So, if *another* state change is requested in that window, it
        // can wind up "resetting" other form values that were just changed.
        //
        // A proper fix involves refactoring the `onSuccess => fetch` structure of the `usePrivacyCenterSettings` API to
        // remove reliance on the backend to tell us the updated form state to use. But that's a larger renovation.
        //
        // For this change, just try throttling to 2G speed and clicking as fast as you can on the checkbox with/without
        // this, you'll see what I mean :) overall this sleep solves POL-5162.
        setTimeout(() => {
          setHasRequestInFlight(false);
        }, 1000);
      }
    };
  }, []);

  const [showOnboardingModal, setShowOnboardingModal] = useState(true);

  const openPreviewNotice = usePrivacyCenterPreviewPopup("/privacy-request");

  const didFinishDSARFormsOnboarding =
    devInstructionsAnswers &&
    devInstructionsAnswers[DevInstructionsDSARFormsOnboardingSlug] === "true";

  // Wait for settings to load before rendering
  if (!settings || !privacyCenter) {
    return <StandardPageWrapper fullWidth />;
  }

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        !didFinishDSARFormsOnboarding && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with DSAR Forms"
                nextOnClick={async () => {
                  await setDevInstructionsAnswer(DevInstructionsDSARFormsOnboardingSlug, "true");
                  history.push(Routes.privacyCenter.publish);
                }}
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary header-sticky">
        <div className="flex items-end justify-between">
          <Breadcrumbs title="DSAR Forms" sourceRoute={Routes.privacyCenter.root} />
          <Button color="secondary" onClick={openPreviewNotice}>
            <span className="flex items-center">
              <div style={{ marginRight: "8px" }}>
                <EyeIcon className="h-6 w-6 text-primary-600" />
              </div>
              Preview
            </span>
          </Button>
        </div>
      </div>

      {showOnboardingModal && !didFinishDSARFormsOnboarding && (
        <FirstTimeDSARFormsModal closeModal={() => setShowOnboardingModal(false)} />
      )}

      <div className="dsar-forms-content" style={{ maxWidth: "895px" }}>
        <div className="tw-boundary">
          <div className="text-neutral-900 text-[18px] font-normal mt-[24px] mb-[48px]">
            Default Privacy Request Form pages are already compliant - you can make further changes
            to the pages to fit your business's needs.
          </div>
        </div>

        <div>
          <hr className="my-lg" />

          <PrivacyCenterRequestFormSettingsRow
            customFieldEnabled={settings.customFieldEnabled}
            customFieldHelp={settings.customFieldHelp}
            customFieldLabel={settings.customFieldLabel}
            customFieldEnabledType={CustomFieldEnabledType.REQUEST_FORM}
            handleFormSubmit={wrapRequestHandler(settings.handleFormSubmit)}
            extendedAddressFormValue={settings.showExtendedAddressFormForRequests}
            extendedAddressFormKey="showExtendedAddressFormForRequests"
            showExtendedAddressForm={true}
            phoneNumberFormValue={settings.showPhoneNumberFormForRequests}
            phoneNumberFormKey="showPhoneNumberFormForRequests"
            showPhoneNumberForm={true}
            showCustomField={true}
            otherFormsSubmitting={hasRequestInFlight}
          />

          {showOptOutFormField && (
            <>
              <hr className="my-lg" />
              <PrivacyCenterOptOutSettingsRow
                customFieldEnabled={settings.customOptOutFieldEnabled}
                customFieldHelp={settings.customOptOutFieldHelp}
                customFieldLabel={settings.customOptOutFieldLabel}
                customFieldEnabledType={CustomFieldEnabledType.OPT_OUT}
                optOutConfirmationMessage={settings.optOutConfirmationMessage}
                handleFormSubmit={wrapRequestHandler(settings.handleFormSubmit)}
                showOptOutConfirmationMessageField={true}
                extendedAddressFormValue={settings.showExtendedAddressFormForOptOut}
                extendedAddressFormKey="showExtendedAddressFormForOptOut"
                showExtendedAddressForm={
                  privacyCenter?.optOutFormOptions?.optOutType === OptOutType.LONG
                }
                phoneNumberFormValue={settings.showPhoneNumberFormForOptOut}
                phoneNumberFormKey="showPhoneNumberFormForOptOut"
                showPhoneNumberForm={
                  privacyCenter?.optOutFormOptions?.optOutType === OptOutType.LONG
                }
                showCustomField={privacyCenter?.optOutFormOptions?.optOutType === OptOutType.LONG}
                otherFormsSubmitting={hasRequestInFlight}
              />
              {showOptOutNoticeEditor && settings.optOut?.notice && (
                <div style={{ marginTop: "32px" }}>
                  <PrivacyCenterOptOutNoticeEditorRow
                    label={null}
                    notice={settings.optOut.notice}
                  />
                </div>
              )}
            </>
          )}

          {(settings.includeResidencyFields ?? true) && (
            <>
              <hr className="my-lg" />
              <PrivacyCenterResidencySettingsRow
                residencySettingsFields={{
                  hideLocationDropdownOnRequestForm: settings.hideLocationDropdownOnRequestForm,
                  addOtherOptionToLocationDropdownOnRequestForm:
                    settings.addOtherOptionToLocationDropdownOnRequestForm,
                }}
                handleFormSubmit={settings.handleFormSubmit}
              />
            </>
          )}
        </div>
      </div>
    </StandardPageWrapper>
  );
}

function FirstTimeDSARFormsModal({ closeModal }: { closeModal: () => void }) {
  return (
    <Modal
      title="Make changes to your DSAR forms"
      body="Your request forms are ready to go using default settings. You can add or customize fields, or skip this step for now."
      alignText="center"
      firstButton="Continue"
      firstButtonColor="primary"
      alignButton="center"
      widthButton="full"
      onClose={closeModal}
      showOverlay
      closeButton
    >
      <img
        src="/assets/images/stay_compliant/privacy_center/dsar-forms-first-time-modal-illustration.svg"
        className="mx-auto w-[268px] h-[211px]"
      />
    </Modal>
  );
}
